/**
 * CompletenessRail — the quiet "Make it a complete trip" section on trip detail.
 *
 * Derives nothing itself: it renders the completeness elements the backend
 * computes from the flights + dates, and lets the user resolve/dismiss each in
 * one tap. AI pre-fill (a suggested transport choice, a shared-ride note, a
 * conference-hotel deadline) rides along on each element's `suggestion`.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Car, Hotel, Ticket, Check, ChevronDown, CircleDashed, Sparkles, RotateCcw,
} from "lucide-react";
import {
  completenessApi,
  type CompletenessElement,
  type CompletenessElementKey,
  type CompletenessResult,
} from "@/lib/api";
import type { ItineraryResponse } from "@/lib/types";
import { hasMatchingItem } from "./completeness-plan";

interface Props {
  tripId: string;
  /**
   * Open the add flow for this gap. When absent the rail degrades to the old
   * answer-only behaviour rather than showing an action that does nothing.
   */
  onPlan?: (el: CompletenessElement) => void;
  /** Used only to tell "answered" apart from "actually on the itinerary". */
  itinerary?: ItineraryResponse | null;
}

function iconFor(el: CompletenessElement) {
  if (el.kind === "lodging") return Hotel;
  if (el.kind === "activities") return Ticket;
  return Car;
}

function resolvedLabel(el: CompletenessElement): string {
  if (el.choice) {
    const opt = el.options.find((o) => o.value === el.choice);
    if (opt) return opt.label;
    return el.choice;
  }
  return "Done";
}

export default function CompletenessRail({ tripId, onPlan, itinerary }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ["completeness", tripId],
    queryFn: () => completenessApi.get(tripId),
  });

  const mutate = useMutation({
    mutationFn: (vars:
      | { op: "set"; key: CompletenessElementKey; status: "resolved" | "dismissed"; choice?: string | null }
      | { op: "clear"; key: CompletenessElementKey }) =>
      vars.op === "set"
        ? completenessApi.set(tripId, vars.key, { status: vars.status, choice: vars.choice ?? null })
        : completenessApi.clear(tripId, vars.key),
    onSuccess: (result: CompletenessResult) => {
      qc.setQueryData(["completeness", tripId], result);
    },
  });

  if (isLoading || !data || data.elements.length === 0) return null;

  const { elements, doneCount, totalCount } = data;
  // Once everything is handled, collapse to a single satisfied line.
  const allDone = doneCount === totalCount;

  return (
    <div className="rounded-xl border border-border bg-card/50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        aria-expanded={open}
      >
        <Sparkles className="h-4 w-4 text-[hsl(var(--primary))]" aria-hidden="true" />
        <span className="text-sm font-semibold">
          {allDone ? "Your trip is fully mapped" : "Make it a complete trip"}
        </span>
        <span className="ml-auto text-xs text-muted-foreground">{doneCount} of {totalCount} done</span>
        <ChevronDown className={"h-4 w-4 text-muted-foreground transition-transform " + (open ? "rotate-180" : "")} />
      </button>

      {open && (
        <div className="space-y-1 px-2 pb-2">
          {elements.map((el) => (
            <ElementRow
              key={el.key}
              el={el}
              busy={mutate.isPending}
              onPlan={onPlan ? () => onPlan(el) : undefined}
              covered={hasMatchingItem(el, itinerary)}
              onChoose={(choice) => mutate.mutate({ op: "set", key: el.key, status: "resolved", choice })}
              onDismiss={() => mutate.mutate({ op: "set", key: el.key, status: "dismissed" })}
              onUndo={() => mutate.mutate({ op: "clear", key: el.key })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface RowProps {
  el: CompletenessElement;
  busy: boolean;
  /** Primary action: go and actually add the thing. */
  onPlan?: () => void;
  /** True when the itinerary already carries something that covers this gap. */
  covered: boolean;
  onChoose: (choice: string) => void;
  onDismiss: () => void;
  onUndo: () => void;
}

function ElementRow({ el, busy, onPlan, covered, onChoose, onDismiss, onUndo }: RowProps) {
  const Icon = iconFor(el);
  const resolved = el.status === "resolved";
  const dismissed = el.status === "dismissed";
  const suggestedNote = el.suggestion?.note;
  const suggestedChoice = el.suggestion?.suggestedChoice ?? null;
  const hotelDeadline = el.suggestion?.hotelDeadline;
  const hotelName = el.suggestion?.hotelName;

  return (
    <div className={"rounded-lg px-2 py-2 " + (el.status === "open" ? "" : "opacity-80")}>
      <div className="flex items-center gap-2.5 text-sm">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center">
          {resolved ? (
            <Check className="h-4 w-4 text-emerald-600" />
          ) : dismissed ? (
            <CircleDashed className="h-4 w-4 text-muted-foreground/50" />
          ) : (
            <Icon className="h-4 w-4 text-muted-foreground" />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={"font-medium " + (dismissed ? "line-through text-muted-foreground" : "")}>{el.label}</span>
            <span className="truncate text-xs text-muted-foreground">{el.hint}</span>
          </div>

          {/* Open: one-tap choices + the AI suggestion */}
          {el.status === "open" && (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {/* The primary move is now to ADD the thing, not to answer a
                  question about it. The one-tap answers stay for the traveler
                  who already sorted it elsewhere — they are just no longer the
                  only way to make the rail go quiet. */}
              {onPlan && (
                <button
                  disabled={busy}
                  onClick={onPlan}
                  className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--foreground))] px-2.5 py-0.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 motion-reduce:transition-none"
                >
                  <Sparkles className="h-3 w-3" aria-hidden="true" />
                  Plan it
                </button>
              )}
              {el.options.map((opt) => {
                const isSuggested = suggestedChoice === opt.value;
                return (
                  <button
                    key={opt.value}
                    disabled={busy}
                    onClick={() => onChoose(opt.value)}
                    className={
                      "rounded-full border px-2.5 py-0.5 text-xs transition-colors disabled:opacity-50 " +
                      (isSuggested
                        ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-foreground font-medium"
                        : "border-border bg-background text-muted-foreground hover:bg-muted")
                    }
                  >
                    {isSuggested && <Sparkles className="mr-1 inline h-3 w-3 text-[hsl(var(--primary))]" />}
                    {opt.label}
                  </button>
                );
              })}
              <button
                disabled={busy}
                onClick={onDismiss}
                className="rounded-full px-2 py-0.5 text-xs text-muted-foreground/60 hover:text-muted-foreground disabled:opacity-50"
                aria-label={"Not needed: " + el.label}
              >
                not needed
              </button>
            </div>
          )}

          {/* Resolved: show the choice + undo */}
          {resolved && !covered && (
            <div className="mt-0.5 text-xs text-amber-700">
              Answered, but nothing for this is on your itinerary yet.
            </div>
          )}

          {resolved && (
            <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
              <span>{resolvedLabel(el)}</span>
              <button disabled={busy} onClick={onUndo} className="inline-flex items-center gap-1 hover:text-foreground disabled:opacity-50">
                <RotateCcw className="h-3 w-3" /> undo
              </button>
            </div>
          )}

          {dismissed && (
            <div className="mt-0.5 text-xs text-muted-foreground">
              <button disabled={busy} onClick={onUndo} className="inline-flex items-center gap-1 hover:text-foreground disabled:opacity-50">
                <RotateCcw className="h-3 w-3" /> undo
              </button>
            </div>
          )}

          {/* AI enrichment: a grounded note / hotel deadline, shown only when present */}
          {el.status === "open" && (suggestedNote || hotelName || hotelDeadline) && (
            <div className="mt-1 flex items-start gap-1 text-xs text-[hsl(var(--primary))]">
              <Sparkles className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                {suggestedNote}
                {hotelName ? (suggestedNote ? " " : "") + hotelName : ""}
                {hotelDeadline ? ` — best rate ends ${hotelDeadline}` : ""}
              </span>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
